import React, { useEffect, useMemo, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { GET_ATTENDANCE_BY_CLASS, GET_CLASS_CANCELLATION_REQUESTS, GET_CLASS_LIST, GET_RECENT_BILLING, GET_SUPERADMIN_DASHBOARD_DATA, GET_SUPERADMIN_REVENUE_INSIGHTS, ME } from "../graphql/query";
import CenteredLoader from "./CenteredLoader";
import AppDatePicker from "./AppDatePicker";
import { formatTimeAgo } from "../utils/time";
import { Link, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  OpenInNew,
  PersonAddAlt1Outlined,
  PlaceOutlined,
  SchoolOutlined,
} from "@mui/icons-material";
import { Calendar } from "lucide-react";
import { HealthiconsITrainingClass } from "./Healthicontraining";
import RevenueInsightsChart, { RevenueInsightPoint } from "./RevenueInsightsChart";
import themestyle from "../theme";
import TruncatedTitle from "./TruncatedTitle";
import { GetClassCancellationRequestsResponse } from "../interfaces/attendance.interface";

type StatCard = {
  label: string;
  value: string;
  path: string;
  chipLabel?: string;
  chipColor?: "success" | "info" | "warning";
  readOnly?: boolean;
};

type AttendanceWidgetItem = {
  id: string;
  className: string;
  instructorName: string;
  time: string;
  startTime: string;
  endTime: string;
  presentCount: number;
  absentCount: number;
  totalCount: number;
};

const STAT_CARDS: StatCard[] = [
  { label: "Total Students", value: "4", chipColor: "success", path: "/students" },
  { label: "Instructors", value: "2", chipColor: "info", path: "/instructors" },
  { label: "Admins", value: "1", path: "/admin" },
  { label: "Offers", value: "2", chipLabel: "3 ending", chipColor: "warning", path: "/offers" },
  { label: "Passes", value: "12", chipColor: "info", path: "/passes" },
  { label: "Monthly Revenue", value: "₹420", chipColor: "success", path: "/billing" },
];

const QUICK_ACTIONS = [
  {
    label: "Add Student",
    path: "/students/add",
    icon: <PersonAddAlt1Outlined sx={{ color: "#0B5D3D", fontSize: 28, flexShrink: 0 }} />,
  },
  {
    label: "Add Instructor",
    path: "/instructors/add",
    icon: <SchoolOutlined sx={{ color: "#0B5D3D", fontSize: 28, flexShrink: 0 }} />,
  },
  {
    label: "Add Class",
    path: "/classes/add",
    icon: <HealthiconsITrainingClass width={28} height={28} style={{ color: "#0B5D3D", flexShrink: 0 }} />,
  },
  {
    label: "Add Event",
    path: "/events/add",
    icon: <Calendar size={28} color="#0B5D3D" style={{ flexShrink: 0 }} />,
  },
] as const;

function formatBillingDate(dateStr: string) {
  if (!dateStr) return "---";
  const date = new Date(dateStr);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  const timeStr = date.toLocaleString("en-US", options);
  if (isToday) {
    return `Today • ${timeStr}`;
  }
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
  };
  return `${date.toLocaleString("en-US", dateOptions)} • ${timeStr}`;
}

const getBadgeStyles = (type: string) => {
  switch (type) {
    case "PASS":
      return { bgcolor: "#E6F4EA", color: "#1E8E3E", border: "1px solid #CEEAD6" };
    case "EVENT":
      return { bgcolor: "#E8F0FE", color: "#1967D2", border: "1px solid #D2E3FC" };
    case "BUNDLE":
      return { bgcolor: "#FEF7E0", color: "#B06000", border: "1px solid #FFEFC3" };
    default:
      return { bgcolor: "#F1F3F4", color: "#5F6368", border: "1px solid #E8EAED" };
  }
};

function initials(fullName: string) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return (first + last).toUpperCase();
}

function registrationStatusStyle(status: "ACTIVE" | "DEACTIVE") {
  if (status === "ACTIVE") {
    return {
      accent: themestyle.colors.success,
      chipBg: themestyle.colors.lightGreenBg,
      chipText: themestyle.colors.success,
      chipBorder: themestyle.colors.success,
    };
  }
  return {
    accent: "#E74C3C",
    chipBg: "#FFF7ED",
    chipText: "#E74C3C",
    chipBorder: "rgb(228, 106, 106)",
  };
}

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "---";
  let d = new Date(dateStr);
  if (isNaN(d.getTime()) && !isNaN(Number(dateStr))) {
    d = new Date(Number(dateStr));
  }
  if (isNaN(d.getTime())) return "---";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

const dashboardCardSx = {
  border: `1px solid ${themestyle.colors.bordercolor}`,
  borderRadius: "18px",
  boxShadow: "0px 16px 40px rgba(7, 24, 51, 0.06)",
};

function formatDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isValidDateInputValue(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00`);
  return !Number.isNaN(parsed.getTime()) && formatDateInputValue(parsed) === value;
}

export default function SuperAdminDashboard() {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const [rangeStart, setRangeStart] = useState<string>(() => {
    const now = new Date();
    return formatDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [rangeEnd, setRangeEnd] = useState<string>(() => formatDateInputValue(new Date()));
  const [attendanceWidgetItems, setAttendanceWidgetItems] = useState<AttendanceWidgetItem[]>([]);
  const [attendanceWidgetLoading, setAttendanceWidgetLoading] = useState(false);
  const today = useMemo(() => formatDateInputValue(new Date()), []);
  const { data: userData } = useQuery(ME);
  const userRole = userData?.me?.role;
  const { data, loading } = useQuery(GET_SUPERADMIN_DASHBOARD_DATA, {
    fetchPolicy: "network-only",
  });
  const { data: billingData, loading: billingLoading } = useQuery(GET_RECENT_BILLING, {
    variables: { limit: 3 },
    fetchPolicy: "network-only",
  });
  const { data: pendingApprovalData } = useQuery<GetClassCancellationRequestsResponse>(
    GET_CLASS_CANCELLATION_REQUESTS,
    {
      variables: { status: "PENDING" },
      skip: userRole !== "SUPER_ADMIN",
      fetchPolicy: "network-only",
    },
  );
  const hasRangeStart = rangeStart.trim().length > 0;
  const hasRangeEnd = rangeEnd.trim().length > 0;
  const canResetRevenueDateRange = hasRangeStart || hasRangeEnd;
  const isRangeStartValid = !hasRangeStart || isValidDateInputValue(rangeStart);
  const isRangeEndValid = !hasRangeEnd || isValidDateInputValue(rangeEnd);
  const isRevenueRangeValid =
    hasRangeStart &&
    hasRangeEnd &&
    isRangeStartValid &&
    isRangeEndValid &&
    new Date(`${rangeStart}T00:00:00`).getTime() <= new Date(`${rangeEnd}T00:00:00`).getTime();
  const { data: revenueInsightsData, loading: revenueInsightsLoading } = useQuery(GET_SUPERADMIN_REVENUE_INSIGHTS, {
    variables: { startDate: rangeStart, endDate: rangeEnd },
    skip: userRole !== "SUPER_ADMIN" || !isRevenueRangeValid,
    fetchPolicy: "network-only",
  });

  const dashboardData = data?.getSuperAdminDashboardData;

  const stats = useMemo(() => {
    if (!dashboardData) return null;
    return {
      students: dashboardData.counts.students,
      instructors: dashboardData.counts.instructors,
      admins: dashboardData.counts.admins,
      classes: dashboardData.counts.classes,
      offers: dashboardData.counts.offers,
      passes: dashboardData.counts.passes,
      events: dashboardData.counts.events,
      monthlyRevenue: dashboardData.counts.monthlyRevenue,
    };
  }, [dashboardData]);

  const instructorActivities = useMemo(() => {
    return dashboardData?.instructorActivities || [];
  }, [dashboardData]);

  const dashboardOffers = useMemo(() => {
    return dashboardData?.recentOffers || [];
  }, [dashboardData]);

  const dashboardEvents = useMemo(() => {
    return dashboardData?.upcomingEvents || [];
  }, [dashboardData]);

  const dashboardRegistrations = useMemo(() => {
    return (dashboardData?.recentStudents || []).map((s: any) => ({
      id: s.id,
      student: s.fullName,
      category: s.assignToClass?.[0]?.split(" - ")?.[0] || "No Class assigned",
      date: formatDate(s.created_at),
      status: s.isActive ? "ACTIVE" : "DEACTIVE",
    }));
  }, [dashboardData]);

  const { data: todayClassesData } = useQuery(GET_CLASS_LIST, {
    variables: {
      page: 1,
      perPage: 100,
      startDate: today,
      endDate: today,
      includeCancelled: false,
    },
    skip: userRole !== "ADMIN",
    fetchPolicy: "network-only",
  });

  const STAT_CARDS_DYNAMIC: StatCard[] = useMemo(() => {
    const cards: StatCard[] = [
      { label: "Total Students", value: stats ? stats.students.toString() : "0", chipColor: "success" as const, path: "/students" },
      { label: "Instructors", value: stats ? stats.instructors.toString() : "0", chipColor: "info" as const, path: "/instructors" },
      { label: "Admins", value: stats ? stats.admins.toString() : "0", path: "/admin" },
      { label: "Classes", value: stats ? stats.classes.toString() : "0", chipColor: "info" as const, path: "/classes" },
      { label: "Events", value: stats ? stats.events.toString() : "0", chipColor: "success" as const, path: "/events" },
      { label: "Passes", value: stats ? stats.passes.toString() : "0", chipColor: "info" as const, path: "/passes" },
      { label: "Offers", value: stats ? stats.offers.toString() : "0", path: "/offers" },
      { label: "Monthly Revenue", value: stats ? `₹${stats.monthlyRevenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : "₹0", chipColor: "success" as const, path: "/billing", readOnly: userRole === "ADMIN" },
    ];
    if (userRole === "ADMIN") {
      return cards.filter((card) => card.label !== "Admins" && card.label !== "Monthly Revenue");
    }
    return cards;
  }, [stats, userRole]);

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  const getDaysRemaining = (validTill: string) => {
    const now = new Date();
    const end = new Date(validTill);
    const diffInMs = end.getTime() - now.getTime();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    if (diffInDays < 0) return "Expired";
    if (diffInDays === 0) return "Ends today";
    if (diffInDays === 1) return "Ends tomorrow";
    return `Ends in ${diffInDays} days`;
  };

  const formatEventDate = (dateStr: string) => {
    if (!dateStr) return { month: "---", day: "--" };
    const date = new Date(dateStr);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return {
      month: months[date.getMonth()],
      day: date.getDate().toString().padStart(2, "0"),
    };
  };

  const formatTo12Hr = (timeRange: string) => {
    if (!timeRange) return "---";
    return timeRange.split("-").map(time => {
      let [hours, minutes] = time.trim().split(":").map(Number);
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; 
      return `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    }).join(" - ");
  };

  const filteredRevenueSeries: RevenueInsightPoint[] = useMemo(() => {
    if (!isRevenueRangeValid) return [];

    return (revenueInsightsData?.getSuperAdminRevenueInsights?.points || []).map((item: RevenueInsightPoint) => ({
      date: item.date,
      month: item.month,
      pass: Number(item.pass || 0),
      passCount: Number(item.passCount || 0),
      classes: Number(item.classes || 0),
      classesCount: Number(item.classesCount || 0),
      event: Number(item.event || 0),
      eventCount: Number(item.eventCount || 0),
    }));
  }, [isRevenueRangeValid, revenueInsightsData]);

  const handleResetRevenueDateRange = () => {
    setRangeStart("");
    setRangeEnd("");
  };

  const pendingApprovalsCount =
    pendingApprovalData?.getClassCancellationRequests?.data?.length ?? 0;

  useEffect(() => {
    if (userRole !== "ADMIN") {
      setAttendanceWidgetItems([]);
      setAttendanceWidgetLoading(false);
      return;
    }

    const todayClasses = todayClassesData?.getClassList?.data || [];

    if (!todayClasses.length) {
      setAttendanceWidgetItems([]);
      setAttendanceWidgetLoading(false);
      return;
    }

    let cancelled = false;

    const loadAttendanceWidget = async () => {
      setAttendanceWidgetLoading(true);

      try {
        const sortedTodayClasses = [...todayClasses].sort((a: any, b: any) => {
          const aTime = a?.startTime ?? "";
          const bTime = b?.startTime ?? "";
          return aTime.localeCompare(bTime);
        });

        const items = await Promise.all(
          sortedTodayClasses.map(async (cls: any) => {
            const response = await apolloClient.query({
              query: GET_ATTENDANCE_BY_CLASS,
              variables: { classId: cls.id, date: today },
              fetchPolicy: "network-only",
            });

            const attendance = response?.data?.getAttendanceByClass?.data;
            const presentCount = attendance?.presentCount ?? 0;
            const absentCount = attendance?.absentCount ?? 0;
            const derivedTotal = presentCount + absentCount;
            const totalCount = derivedTotal || cls.enrolledStudents?.length || 0;

            return {
              id: cls.id,
              className: cls.className,
              instructorName: cls.instructorName || "Instructor not assigned",
              time: formatTo12Hr(`${cls.startTime}-${cls.endTime}`),
              startTime: cls.startTime,
              endTime: cls.endTime,
              presentCount,
              absentCount,
              totalCount,
            };
          })
        );

        if (!cancelled) {
          setAttendanceWidgetItems(items);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load attendance widget:", error);
          setAttendanceWidgetItems([]);
        }
      } finally {
        if (!cancelled) {
          setAttendanceWidgetLoading(false);
        }
      }
    };

    loadAttendanceWidget();

    return () => {
      cancelled = true;
    };
  }, [apolloClient, today, todayClassesData, userRole]);

  if (loading || !dashboardData) {
    return <CenteredLoader minHeight="60vh" />;
  }

  return (
    <Stack spacing={2.5} sx={{ pt: 2, pb: 2.5 }}>
      <Box>
        <Typography
          sx={{ fontWeight: 700, pt: 2, color: themestyle.colors.primary, fontSize: 26 }}
        >
          Dashboard
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: `repeat(${STAT_CARDS_DYNAMIC.length}, 1fr)`,
          },
          gap: 2.5,
        }}
      >
        {STAT_CARDS_DYNAMIC.map((card) => (
          <Card
            key={card.label}
            elevation={0}
            onClick={card.readOnly ? undefined : () => navigate(card.path)}
            sx={{
              ...dashboardCardSx,
              cursor: card.readOnly ? "default" : "pointer",
              transition: "box-shadow 0.3s ease-in-out, border-color 0.3s ease-in-out",
              "&:hover": card.readOnly ? undefined : {
                boxShadow: "0 12px 24px -10px rgba(0,0,0,0.15)",
                borderColor: themestyle.colors.success,
              },
            }}
          >
            <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1.5}
              >
                <Box>
                  {(() => {
                    const revenueDigits = card.value.startsWith("₹") ? card.value.substring(1) : card.value;
                    const revenueLength = revenueDigits.length;
                    const isMonthlyRevenueCard = card.label === "Monthly Revenue";
                    const monthlyRevenueValueFontSize = revenueLength >= 12
                      ? { xs: "0.76rem", sm: "0.88rem", md: "0.98rem" }
                      : revenueLength >= 10
                        ? { xs: "0.84rem", sm: "0.96rem", md: "1.08rem" }
                        : revenueLength >= 8
                          ? { xs: "0.92rem", sm: "1.04rem", md: "1.16rem" }
                          : { xs: "1rem", sm: "1.15rem", md: "1.25rem" };
                    const monthlyRevenueCurrencyFontSize = revenueLength >= 12
                      ? { xs: "0.68rem", sm: "0.78rem", md: "0.86rem" }
                      : revenueLength >= 10
                        ? { xs: "0.74rem", sm: "0.86rem", md: "0.94rem" }
                        : revenueLength >= 8
                          ? { xs: "0.82rem", sm: "0.94rem", md: "1rem" }
                          : { xs: "0.9rem", sm: "1rem", md: "1.05rem" };

                    return (
                      <>
                  <Typography 
                    sx={{ 
                      color: themestyle.colors.textSecondary, 
                      fontWeight: 800, 
                      fontSize: 13.5,
                      lineHeight: 1.1, 
                      display: "block",
                      letterSpacing: "0.01em",
                      mb: 0.25
                    }}
                  >
                    {card.label}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mt: 0.75, 
                      fontWeight: 900, 
                      color: "#111827", 
                      display: "flex", 
                      alignItems: "baseline",
                      fontSize:
                        isMonthlyRevenueCard
                          ? monthlyRevenueValueFontSize
                          : undefined,
                      lineHeight: 1.1,
                      minWidth: 0,
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                  >
                    {card.value.startsWith("₹") ? (
                      <>
                        <Box
                          component="span"
                          sx={{
                            fontSize: isMonthlyRevenueCard ? monthlyRevenueCurrencyFontSize : "0.7em",
                            mr: isMonthlyRevenueCard ? 0.4 : 0.35,
                            fontWeight: 800,
                            lineHeight: 1,
                            flexShrink: 0,
                          }}
                        >
                          ₹
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            minWidth: 0,
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "clip",
                          }}
                        >
                          {card.value.substring(1)}
                        </Box>
                      </>
                    ) : (
                      card.value
                    )}
                  </Typography>
                      </>
                    );
                  })()}
                </Box>
                {card.chipLabel ? (
                  <Chip
                    size="small"
                    label={card.chipLabel}
                    color={card.chipColor ?? "info"}
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      {userRole !== "ADMIN" ? (
        <Box
          sx={{
            border: `1px solid ${themestyle.colors.bordercolor}`,
            borderRadius: "24px",
            background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAF7 100%)",
            boxShadow: "0px 18px 44px rgba(7, 24, 51, 0.06)",
            px: { xs: 1.5, sm: 2.25 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography
            sx={{
              color: themestyle.colors.primary,
              fontSize: 18,
              fontWeight: 600,
              mb: 1.5,
            }}
          >
            Quick Actions
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 1px 280px" },
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
                gap: 1.25,
              }}
            >
              {QUICK_ACTIONS.map((action) => {
                return (
                  <Box
                    key={action.label}
                    onClick={() => navigate(action.path)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.25,
                      px: { xs: 2, lg: 1.5 },
                      py: 1.35,
                      borderRadius: "22px",
                      bgcolor: "#FFFFFF",
                      border: "1px solid rgba(163, 214, 92, 0.16)",
                      boxShadow: "0px 10px 24px rgba(7, 24, 51, 0.05)",
                      cursor: "pointer",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        borderColor: "#a3d65c",
                        boxShadow: "0px 14px 28px rgba(7, 24, 51, 0.08)",
                      },
                    }}
                  >
                    {action.icon}
                    <Typography
                      sx={{
                        color: "#252B37",
                        fontWeight: 700,
                        fontSize: { xs: 15, lg: 14 },
                        whiteSpace: "nowrap",
                        textAlign: "center",
                      }}
                    >
                      {action.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                display: { xs: "none", lg: "block" },
                width: "1px",
                height: 52,
                bgcolor: "#E5E7EB",
                justifySelf: "center",
              }}
            />

            <Box
              onClick={() => navigate("/calendar", { state: { activeTab: "cancellations" } })}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "space-between", lg: "flex-start" },
                gap: { xs: 2, lg: 2.5 },
                pl: { xs: 0, lg: 2.25 },
                pt: { xs: 0.5, lg: 0 },
                minWidth: 0,
                cursor: "pointer",
                transition: "opacity 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  opacity: 0.85,
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Typography
                sx={{
                  color: "#5F6B7A",
                  fontSize: { xs: 16, lg: 15 },
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                Pending Approvals
              </Typography>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  bgcolor: "#a3d65c",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {pendingApprovalsCount}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2.5 }}>
        {userRole !== "ADMIN" ? (
          <Card
            elevation={0}
            sx={{ ...dashboardCardSx, overflow: "hidden" }}
          >
            <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 18, color: themestyle.colors.primary }}>
                  Revenue Insights
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "flex-start" }}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  <Box sx={{ minWidth: 220 }}>
                    <Typography
                      sx={{
                        mb: 0.75,
                        color: "#111827",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      From Date
                    </Typography>
                    <AppDatePicker
                      value={rangeStart}
                      onChange={(v) => setRangeStart(v)}
                      error={(hasRangeStart && !isRangeStartValid) || (hasRangeStart && hasRangeEnd && isRangeStartValid && isRangeEndValid && !isRevenueRangeValid)}
                      helperText={
                        hasRangeStart && !isRangeStartValid
                          ? "Enter a valid date"
                          : hasRangeStart && hasRangeEnd && isRangeStartValid && isRangeEndValid && !isRevenueRangeValid
                            ? "From Date must be on or before To Date"
                            : " "
                      }
                    />
                  </Box>
                  <Box sx={{ minWidth: 220 }}>
                    <Typography
                      sx={{
                        mb: 0.75,
                        color: "#111827",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      To Date
                    </Typography>
                    <AppDatePicker
                      value={rangeEnd}
                      onChange={(v) => setRangeEnd(v)}
                      error={(hasRangeEnd && !isRangeEndValid) || (hasRangeStart && hasRangeEnd && isRangeStartValid && isRangeEndValid && !isRevenueRangeValid)}
                      helperText={
                        hasRangeEnd && !isRangeEndValid
                          ? "Enter a valid date"
                          : hasRangeStart && hasRangeEnd && isRangeStartValid && isRangeEndValid && !isRevenueRangeValid
                            ? "To Date must be on or after From Date"
                            : " "
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: { xs: "stretch", sm: "flex-start" },
                      minWidth: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Typography
                      sx={{
                        mb: 0.75,
                        fontWeight: 600,
                        fontSize: 14,
                        visibility: "hidden",
                      }}
                    >
                      Reset
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleResetRevenueDateRange}
                      disabled={!canResetRevenueDateRange}
                      sx={{
                        minWidth: { xs: "100%", sm: 92 },
                        height: 40,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 700,
                        color: canResetRevenueDateRange ? themestyle.colors.primary : "#9CA3AF",
                        borderColor: canResetRevenueDateRange ? themestyle.colors.bordercolor : "#D1D5DB",
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
                </Stack>
              </Stack>
              <RevenueInsightsChart
                series={filteredRevenueSeries}
                fullWidth={true}
                loading={revenueInsightsLoading}
              />
            </CardContent>
          </Card>
        ) : (
          <Card
            elevation={0}
            sx={dashboardCardSx}
          >
            <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 18, color: themestyle.colors.primary }}>
                  Recent Student Registrations
                </Typography>
                <Button
                  variant="text"
                  onClick={() => navigate("/students")}
                  sx={{ textTransform: "none", fontWeight: 800, color: themestyle.colors.success }}
                >
                  See full list
                </Button>
              </Stack>

              <Box sx={{ overflowX: "auto" }}>
                <Table
                  size="small"
                  sx={{
                    minWidth: { xs: 560, sm: 520 },
                    borderCollapse: "separate",
                    borderSpacing: "0 10px",
                    "& thead th": { borderBottom: "none", pb: 1 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>Student</TableCell>
                      <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>
                        Class Category
                      </TableCell>
                      <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>
                        Date Joined
                      </TableCell>
                      <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardRegistrations.length > 0 ? (
                      dashboardRegistrations.map((r: any) => (
                        <TableRow
                          key={r.id}
                          onClick={() => navigate(`/students/report/${r.id}`)}
                          sx={{
                            cursor: "pointer",
                            "& td": {
                              bgcolor: "#FFFFFF",
                              borderTop: `1px solid ${themestyle.colors.bordercolor}`,
                              borderBottom: `1px solid ${themestyle.colors.bordercolor}`,
                              py: 1.5,
                            },
                            "& td:first-of-type": {
                              borderLeft: `1px solid ${themestyle.colors.bordercolor}`,
                              borderTopLeftRadius: 14,
                              borderBottomLeftRadius: 14,
                            },
                            "& td:last-of-type": {
                              borderRight: `1px solid ${themestyle.colors.bordercolor}`,
                              borderTopRightRadius: 14,
                              borderBottomRightRadius: 14,
                            },
                            "&:hover td": { bgcolor: themestyle.colors.primarybackground },
                          }}
                        >
                          <TableCell sx={{ overflow: "hidden" }}>
                            <Stack direction="row" alignItems="center" spacing={1.25}>
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: "#a3d65c",
                                  color: "#fff",
                                  fontWeight: 900,
                                  fontSize: 14,
                                  boxShadow: "0 4px 12px rgba(163, 214, 92, 0.35)",
                                }}
                              >
                                {initials(r.student)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary }} noWrap>
                                  {r.student}
                                </Typography>
                                <Typography variant="caption" sx={{ color: themestyle.colors.textMuted, fontWeight: 700 }}>
                                  New registration
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>
                            {r.category}
                          </TableCell>
                          <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 700 }}>
                            {r.date}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.status === "ACTIVE" ? "Active" : "Deactive"}
                              variant="outlined"
                              sx={{
                                fontWeight: 900,
                                borderRadius: 999,
                                bgcolor: registrationStatusStyle(r.status).chipBg,
                                color: registrationStatusStyle(r.status).chipText,
                                borderColor: registrationStatusStyle(r.status).chipBorder,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" sx={{ color: themestyle.colors.textMuted, fontWeight: 700 }}>
                            No recent registrations
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        )}

        <Card
          elevation={0}
          sx={dashboardCardSx}
        >
          <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography sx={{ fontWeight: 600, fontSize: 18, color: themestyle.colors.primary }}>Instructor Activities</Typography>
              <Box
                onClick={() => navigate("/classes")}
                sx={{
                  color: themestyle.colors.success,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "opacity 0.2s ease, transform 0.2s ease",
                  "&:hover": {
                    opacity: 0.8,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <OpenInNew sx={{ fontSize: 20 }} />
              </Box>
            </Stack>
            <List dense sx={{ mt: 1 }}>
              {instructorActivities.length > 0 ? (
                instructorActivities.map((a: any, idx: number) => (
                  <ListItem key={idx} disableGutters sx={{ py: 1 }}>
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: "#a3d65c",
                          color: "#fff",
                          fontWeight: 900,
                          fontSize: 14,
                          boxShadow: "0 4px 12px rgba(163, 214, 92, 0.35)",
                        }}
                      >
                        {initials(a.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          title={a.name}
                          sx={{ fontWeight: 800, color: themestyle.colors.primary, lineHeight: 1.2 }}
                        >
                          {a.name && a.name.length > 16 ? `${a.name.slice(0, 16)}...` : a.name}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" sx={{ color: themestyle.colors.textSecondary, fontWeight: 600 }}>
                            {a.detail}
                          </Typography>
                          <Typography variant="caption" sx={{ color: themestyle.colors.textMuted, fontWeight: 700 }}>
                            {formatTimeAgo(a.time)}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: themestyle.colors.textMuted, fontWeight: 700 }}>
                    No recent activities
                  </Typography>
                </Box>
              )}
            </List>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2.5 }}>
        {userRole === "ADMIN" ? (
          <Card
            elevation={0}
            sx={dashboardCardSx}
          >
            <CardContent sx={{ pt: 2.25, pl: 2.25, pr: 1.5, pb: 2.25, "&:last-child": { pb: 2.25 } }}>
              <Typography sx={{ fontWeight: 600, fontSize: 18, color: themestyle.colors.primary }}>
                Today's Class
              </Typography>

              {attendanceWidgetLoading ? (
                <Box
                  sx={{
                    minHeight: 220,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ color: themestyle.colors.textSecondary, fontWeight: 700 }}>
                    Loading attendance...
                  </Typography>
                </Box>
              ) : attendanceWidgetItems.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 220,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: themestyle.colors.textSecondary,
                      fontWeight: 700,
                      fontSize: 16,
                    }}
                  >
                    No classes scheduled for today
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    mt: 1.5,
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 1.25,
                  }}
                >
                  {attendanceWidgetItems.map((item) => (
                    <Card
                      key={item.id}
                      elevation={0}
                      onClick={() =>
                        navigate(`/calendar/${encodeURIComponent(item.id)}`, {
                          state: {
                            event: {
                              id: item.id,
                              classId: item.id,
                              title: item.className,
                              start: new Date(`${today}T${item.startTime}`),
                              end: new Date(`${today}T${item.endTime}`),
                            },
                          },
                        })
                      }
                      sx={{
                        ...dashboardCardSx,
                        borderRadius: "20px",
                        background: "#FFFFFF",
                        cursor: "pointer",
                        transition: "box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
                        "&:hover": {
                          borderColor: themestyle.colors.success,
                          boxShadow: "0 14px 30px rgba(7, 24, 51, 0.08)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 1.5, sm: 1.75 },
                          minHeight: 116,
                          display: "flex",
                          flexDirection: "column",
                          "&:last-child": { pb: { xs: 1.5, sm: 1.75 } },
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              color: themestyle.colors.primary,
                              fontSize: { xs: 17, sm: 18 },
                              lineHeight: 1.1,
                            }}
                            noWrap
                          >
                            {item.className}
                          </Typography>
                        </Box>

                        <Box sx={{ minWidth: 0, mt: "auto" }}>
                          <Typography
                            sx={{
                              color: themestyle.colors.textSecondary,
                              fontWeight: 700,
                              display: "block",
                              fontSize: { xs: 11, sm: 12 },
                              lineHeight: 1.2,
                            }}
                          >
                            {item.instructorName}
                          </Typography>
                          <Typography
                            sx={{
                              color: themestyle.colors.textMuted,
                              fontWeight: 700,
                              display: "block",
                              mt: 0.35,
                              fontSize: { xs: 10, sm: 11 },
                              lineHeight: 1.15,
                            }}
                          >
                            {item.time}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: "auto" }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                color: themestyle.colors.textMuted,
                                fontWeight: 700,
                                fontSize: { xs: 10, sm: 11 },
                                lineHeight: 1.2,
                              }}
                            >
                              Present
                            </Typography>
                            <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary, fontSize: { xs: 14, sm: 15 }, mt: 0.1 }}>
                              {item.presentCount === 0 ? "-" : item.presentCount}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                color: themestyle.colors.textMuted,
                                fontWeight: 700,
                                fontSize: { xs: 10, sm: 11 },
                                lineHeight: 1.2,
                              }}
                            >
                              Absent
                            </Typography>
                            <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary, fontSize: { xs: 14, sm: 15 }, mt: 0.1 }}>
                              {item.absentCount === 0 ? "-" : item.absentCount}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                color: themestyle.colors.textMuted,
                                fontWeight: 700,
                                fontSize: { xs: 10, sm: 11 },
                                lineHeight: 1.2,
                              }}
                            >
                              Total
                            </Typography>
                            <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary, fontSize: { xs: 14, sm: 15 }, mt: 0.1 }}>
                              {item.totalCount === 0 ? "-" : item.totalCount}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card
            elevation={0}
            sx={dashboardCardSx}
          >
            <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 18, color: themestyle.colors.primary }}>
                  Recent Student Registrations
                </Typography>
                <Button
                  variant="text"
                  onClick={() => navigate("/students")}
                  sx={{ textTransform: "none", fontWeight: 800, color: themestyle.colors.success }}
                >
                  See full list
                </Button>
              </Stack>

              <Box sx={{ overflowX: "auto" }}>
                <Table
                  size="small"
                  sx={{
                    minWidth: { xs: 560, sm: 520 },
                    borderCollapse: "separate",
                    borderSpacing: "0 10px",
                    "& thead th": { borderBottom: "none", pb: 1 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>Student</TableCell>
                      <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>
                        Class Category
                      </TableCell>
                      <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>
                        Date Joined
                      </TableCell>
                      <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardRegistrations.length > 0 ? (
                      dashboardRegistrations.map((r: any) => (
                        <TableRow
                          key={r.id}
                          onClick={() => navigate(`/students/report/${r.id}`)}
                          sx={{
                            cursor: "pointer",
                            "& td": {
                              bgcolor: "#FFFFFF",
                              borderTop: `1px solid ${themestyle.colors.bordercolor}`,
                              borderBottom: `1px solid ${themestyle.colors.bordercolor}`,
                              py: 1.5,
                            },
                            "& td:first-of-type": {
                              borderLeft: `1px solid ${themestyle.colors.bordercolor}`,
                              borderTopLeftRadius: 14,
                              borderBottomLeftRadius: 14,
                            },
                            "& td:last-of-type": {
                              borderRight: `1px solid ${themestyle.colors.bordercolor}`,
                              borderTopRightRadius: 14,
                              borderBottomRightRadius: 14,
                            },
                            "&:hover td": { bgcolor: themestyle.colors.primarybackground },
                          }}
                        >
                          <TableCell sx={{ overflow: "hidden" }}>
                            <Stack direction="row" alignItems="center" spacing={1.25}>
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: "#a3d65c",
                                  color: "#fff",
                                  fontWeight: 900,
                                  fontSize: 14,
                                  boxShadow: "0 4px 12px rgba(163, 214, 92, 0.35)",
                                }}
                              >
                                {initials(r.student)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary }} noWrap>
                                  {r.student}
                                </Typography>
                                <Typography variant="caption" sx={{ color: themestyle.colors.textMuted, fontWeight: 700 }}>
                                  New registration
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>
                            {r.category}
                          </TableCell>
                          <TableCell sx={{ color: themestyle.colors.textSecondary, fontWeight: 700 }}>
                            {r.date}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.status === "ACTIVE" ? "Active" : "Deactive"}
                              variant="outlined"
                              sx={{
                                fontWeight: 900,
                                borderRadius: 999,
                                bgcolor: registrationStatusStyle(r.status).chipBg,
                                color: registrationStatusStyle(r.status).chipText,
                                borderColor: registrationStatusStyle(r.status).chipBorder,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" sx={{ color: themestyle.colors.textMuted, fontWeight: 700 }}>
                            No recent registrations
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        )}

        <Card
          elevation={0}
          sx={{ ...dashboardCardSx, height: "100%", display: "flex", flexDirection: "column" }}
        >
          <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 }, display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 18, color: themestyle.colors.primary }}>Offers</Typography>

            <Stack spacing={1.5} sx={{ mt: 1.25, flexGrow: 1 }}>
              {dashboardOffers.length > 0 ? (
                dashboardOffers.map((o: any) => (
                  <Box 
                    key={o.id} 
                    onClick={() => navigate(`/offers/edit/${o.id}`)}
                    sx={{ 
                      borderRadius: 1.5, 
                      border: `1px solid ${themestyle.colors.bordercolor}`, 
                      p: 1.5,
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: themestyle.colors.success,
                        bgcolor: themestyle.colors.primarybackground,
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)"
                      }
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary }} noWrap>
                          {o.offerName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: themestyle.colors.textSecondary, fontWeight: 700 }}>
                          {o.discount}% Discount
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                          <Chip
                            size="small"
                            label={o.offerCode}
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation(); 
                              handleCopyCode(o.offerCode);
                            }}
                            sx={{ 
                              fontWeight: 900, 
                              color: themestyle.colors.success, 
                              borderColor: themestyle.colors.success,
                              cursor: "pointer",
                              "&:hover": { bgcolor: themestyle.colors.lightGreenBg }
                            }}
                          />
                          <Typography variant="caption" sx={{ color: themestyle.colors.textMuted, fontWeight: 800 }}>
                            {getDaysRemaining(o.validTill)}
                          </Typography>
                        </Stack>
                      </Box>
                      <Box sx={{ width: 3, borderRadius: 2, bgcolor: themestyle.colors.success, mt: 0.25 }} />
                    </Stack>
                  </Box>
                ))
              ) : (
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                  <Typography variant="body2" sx={{ color: themestyle.colors.textMuted, fontWeight: 700 }}>
                    No offers found
                  </Typography>
                </Box>
              )}
            </Stack>

            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate("/offers/add")}
              sx={{
                mt: "auto",
                pt: 1.2,
                pb: 1.2,
                textTransform: "none",
                fontWeight: 900,
                bgcolor: themestyle.colors.buttonGreen,
                "&:hover": { bgcolor: themestyle.colors.buttonGreenHover },
              }}
            >
              Create New Offer
            </Button>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: userRole === "ADMIN" ? "1fr" : "1fr 1fr" }, gap: 2.5 }}>
        {userRole !== "ADMIN" ? (
          <Card
            elevation={0}
            sx={dashboardCardSx}
          >
            <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
              <Typography sx={{ fontWeight: 600, fontSize: 18, color: themestyle.colors.primary }}>Recent Billing</Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                {billingLoading ? (
                  <Typography sx={{ color: "#9CA3AF", fontWeight: 700, textAlign: "center", py: 3 }}>
                    Loading billing...
                  </Typography>
                ) : (billingData?.getRecentBilling?.data || []).length > 0 ? (
                  billingData.getRecentBilling.data.map((b: any, idx: number) => (
                    <Box key={b.id}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box sx={{ minWidth: 0, flex: 1, mr: 3}}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                            <TruncatedTitle
                              text={b.title}
                              variant="body1"
                              maxLines={1}
                              sx={{ fontWeight: 800, color: "#111827", fontSize: "15px" }}
                            />
                            <Box 
                              component="span" 
                              sx={{ 
                                display: "inline-flex",
                                alignItems: "center",
                                px: 0.8,
                                py: 0.15,
                                borderRadius: "6px",
                                fontSize: "10px",
                                fontWeight: 800,
                                textTransform: "uppercase",
                                ...getBadgeStyles(b.itemType)
                              }}
                            >
                              {b.itemType === "BUNDLE" ? `${b.itemCount} ITEMS` : b.itemType}
                            </Box>
                          </Stack>
                          {b.breakdown && (
                            <Typography variant="caption" sx={{ color: "#4B5563", fontWeight: 700, display: "block", mb: 0.25 }}>
                              {b.breakdown}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 600, display: "block" }}>
                            {b.subtitle}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, display: "block", mt: 0.5 }}>
                            {formatBillingDate(b.date)}
                          </Typography>
                        </Box>
                        <Typography sx={{ 
                          fontWeight: 900, 
                          color: "#16A34A", 
                          fontSize: "16px",
                          letterSpacing: "-0.01em" 
                        }}>
                          +₹{b.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Stack>
                      {idx !== billingData.getRecentBilling.data.length - 1 ? <Divider sx={{ mt: 2 }} /> : null}
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ color: "#9CA3AF", fontWeight: 700, textAlign: "center", py: 3 }}>
                    No recent billing
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        <Card
          elevation={0}
          sx={dashboardCardSx}
        >
          <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
            <Typography sx={{ fontWeight: 600, fontSize: 18, color: themestyle.colors.primary }}>Upcoming Events</Typography>
            {dashboardEvents.length === 0 ? (
              <Box
                sx={{
                  minHeight: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    color: themestyle.colors.textSecondary,
                    fontWeight: 700,
                    fontSize: 16,
                  }}
                >
                  No upcoming events
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                {dashboardEvents.map((e: any, idx: number) => {
                  const dateInfo = formatEventDate(e.startDate);
                  return (
                    <Box key={e.id}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 58,
                            borderRadius: 2,
                            border: `1px solid ${themestyle.colors.bordercolor}`,
                            p: 1,
                            textAlign: "center",
                            bgcolor: themestyle.colors.primarybackground,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 900, color: themestyle.colors.primary }}>
                            {dateInfo.month}
                          </Typography>
                          <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary, lineHeight: 1 }}>
                            {dateInfo.day}
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary }} noWrap>
                            {e.eventName}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <PlaceOutlined sx={{ fontSize: 13, color: themestyle.colors.textSecondary }} />
                            <Typography variant="caption" sx={{ color: themestyle.colors.textSecondary, fontWeight: 700 }}>
                              {e.location} • {formatTo12Hr(e.time)}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                      {idx !== dashboardEvents.length - 1 ? <Divider sx={{ mt: 1.25 }} /> : null}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}
